/**
 * The default permission template, and the actions no organization may configure
 * (`docs/design/00-contracts.md` §11.3, §11.3.1; decisions 0001 and 0002).
 *
 * Decision 0001: role permissions are **data**, not code. A superadmin edits them in Settings
 * (Phase 2) and they live in `role_permissions` (§6.6a), one row per (role, resource, verb) per
 * organization. What this file holds is the template a **new** organization is created with —
 * a strong default proposal, not the running truth for any existing organization.
 *
 * One direction, no hand-copying: `ROLES.md` §4 (the product owner's JSON block) →
 * `DEFAULT_PERMISSION_MATRIX` here (the only hand-maintained copy in code) →
 * `packages/db/sql/default_permissions.sql`, generated from it → `app.create_organization()` copies
 * that table into the new organization's rows in the same transaction. `roles-matrix.spec.ts`
 * (T12) asserts the first two agree cell for cell, so the document and the code cannot drift.
 *
 * FOUR COLUMNS, NOT FIVE (sak 232, 0465). The comments below that argue about an `external` cell
 * are history: that column is gone. An external is a personal account that opens what is shared as
 * a `guest`, and a guest's rights are the fixed whitelist in `policy/guest.ts`, never a column here.
 */
import type { OrgRole } from '../enums.js';
import { ORG_ROLES } from '../enums.js';
import type { Action, PermissionRow, ResourceType, Verb } from './types.js';

/**
 * The seven cell values of `ROLES.md` §4. Only the allow/deny bit is stored in the database —
 * the conditional flavours (`own`, `dept`, `part`, `grant`) say *how* `rules.ts` narrows a
 * permitted action for that role, which is code, not configuration. Keeping the flavour in the
 * template is what makes the comparison against `ROLES.md` a cell-for-cell one.
 */
export type PermissionCell =
  /** unconditional */
  | 'yes'
  /** only rows the actor created or is assigned */
  | 'own'
  /** only the actor's departments */
  | 'dept'
  /** only threads the actor participates in */
  | 'part'
  /** only explicitly granted objects, unexpired */
  | 'grant'
  /** denied by default; an organization may switch this on in Settings */
  | 'no'
  /** locked in code; can never be granted to this role through role_permissions */
  | 'locked';

export type RoleCells = Readonly<Record<OrgRole, PermissionCell>>;

/** Written out per role so that a missing role in a row is a compile error, not a silent deny. */
const cells = (
  superadmin: PermissionCell,
  admin: PermissionCell,
  member: PermissionCell,
  limited: PermissionCell,
): RoleCells => ({ superadmin, admin, member, limited });

/**
 * The template, byte-comparable with the `matrix` object of `ROLES.md` §4.
 *
 * Read a row as: this is what a new organization starts with. Every cell that is not `locked` is
 * a starting value the organization may change in Settings from Phase 2.
 */
export const DEFAULT_PERMISSION_MATRIX = {
  // ---------- organization ----------
  'organization:read': cells('yes', 'yes', 'yes', 'yes'),
  'organization:update': cells('yes', 'yes', 'no', 'no'),
  'organization:delete': cells('yes', 'locked', 'locked', 'locked'),
  'organization:manage_roles': cells('yes', 'yes', 'no', 'no'),
  /*
   * 249.10 (Atle 13.10): `organization:export`, `contact:export` og `audit_log:export` er fjernet
   * (0992). Ingenting brukte dem; cella «Eksportere» (`export:create`) sammen med det du ellers ser
   * avgjør en eksport.
   */
  /*
   * «ORGANISASJONER VI SAMARBEIDER MED» (sak 239.21). Å legge til en samarbeidspartner og å svare på
   * en forespørsel fra en annen organisasjon. Samme standard som `organization:update`, som styrte
   * koblingene før — men nå en egen celle, fordi Atle ba om det: «Hvem som kan legge til, er en celle.»
   */
  'organization_partner:create': cells('yes', 'yes', 'no', 'no'),
  'organization_partner:respond': cells('yes', 'yes', 'no', 'no'),

  // ---------- memberships and invitations ----------
  'membership:list': cells('yes', 'yes', 'yes', 'no'),
  'membership:read': cells('yes', 'yes', 'yes', 'no'),
  'membership:create': cells('yes', 'yes', 'no', 'no'),
  'membership:update': cells('yes', 'yes', 'no', 'no'),
  /*
   * TITTEL, ANSVARSOMRÅDE OG «RAPPORTERER TIL» (QA12 #7, sak 134).
   *
   * Egen celle og ikke `membership:update`, fordi de to spør om forskjellige ting: den ene er
   * «hvem er medlem, og med hvilken rolle», den andre er «hva står det om dem». Et hus kan godt
   * ville at flere skal kunne rette en tittel enn kan endre en rolle — og motsatt, at en
   * kontorleder holder profilene i orden uten å kunne slippe noen inn.
   *
   * Rollebytte har allerede sine to celler: `membership:update` og den låste
   * `membership:manage_roles`. En tredje for det samme ville vært en bryter som sier noe
   * can() alt har bestemt (QA12 sak 134).
   */
  'member_profile:update': cells('yes', 'yes', 'no', 'no'),
  /*
   * DET SAMME OM EN SUPERADMIN (sak 239.36, Atle 04.10: «ligger vel i rettighets-innstillinger?»).
   * Standard: nei for alle under superadmin. `member_profile:update` sier om man kan endre en
   * kollegas tittel i det hele tatt; denne sier om det også gjelder en superadmin.
   */
  'superadmin_profile:update': cells('yes', 'no', 'no', 'no'),
  /*
   * Å AVSLUTTE ET MEDLEMSKAP — deaktiver, gjenopprett og fjern, alle tre (QA15 blokk B2).
   *
   * Sto som `yes` for `admin` fram til 22. september, sammen med fotnote 1 i ROLES.md om at en
   * admin kunne fjerne member, limited og external. Cella hadde aldri hatt et endepunkt: den var
   * en reservert verdi, ikke en rett noen brukte. Atle bestemte formen da den fikk et: «Bare
   * superadmin kan gjøre det.»
   *
   * Cella er halve svaret. Den andre halvparten er `onlySuperadminEndsAMembership` i `rules.ts`,
   * som også lukker de husene som allerede har en rad med `admin = true`.
   */
  'membership:delete': cells('yes', 'no', 'no', 'no'),
  'membership:manage_roles': cells('yes', 'locked', 'locked', 'locked'),
  /*
   * F54 (runde 4. oktober): medarbeider ser ventende invitasjoner, uten å kunne trekke dem tilbake
   * (`invitation:delete` står for seg). Justerbar siden 0721.
   */
  'invitation:list': cells('yes', 'yes', 'yes', 'no'),
  'invitation:create': cells('yes', 'yes', 'no', 'no'),
  'invitation:delete': cells('yes', 'yes', 'no', 'no'),

  // ---------- contacts ----------
  'contact:list': cells('yes', 'yes', 'yes', 'dept'),
  'contact:read': cells('yes', 'yes', 'yes', 'yes'),
  'contact:create': cells('yes', 'yes', 'yes', 'no'),
  'contact:update': cells('yes', 'yes', 'yes', 'no'),
  'contact:delete': cells('yes', 'yes', 'no', 'no'),
  'contact:invite': cells('yes', 'yes', 'no', 'no'),
  'contact:merge': cells('yes', 'no', 'no', 'no'),

  // ---------- festivals ----------
  'festival:list': cells('yes', 'yes', 'yes', 'yes'),
  'festival:read': cells('yes', 'yes', 'yes', 'yes'),
  'festival:create': cells('yes', 'yes', 'no', 'no'),
  'festival:update': cells('yes', 'yes', 'no', 'no'),
  'festival:delete': cells('yes', 'no', 'no', 'no'),
  /*
   * HVEM SOM KAN GI TILGANG TIL EN FESTIVAL (sak 182, punkt 5 — Atles åpne delspørsmål).
   *
   * Atle: «ÅPEN — avgjøres når vi går gjennom Rettigheter i dybden (blir trolig en innstilling
   * "hvem kan gjøre endringer"). Til da: Superadmin og Administrator, som i dag. Bygg det slik at
   * dette senere kan bli en innstilling uten ny modell.»
   *
   * Det er nøyaktig hva en celle er. Verdien her ER dagens oppførsel, og den dagen han vil flytte
   * grensen er det et klikk i den samme matrisen — ingen ny tabell, ingen migrering, ingen kode.
   *
   * `manage_roles` og ikke `share`: å gi noen tilgang til en festival er å si hvem som hører til,
   * ikke å dele et objekt med en utenforstående. `share` er `object_grants`, som har utløpsdato.
   */
  'festival:manage_roles': cells('yes', 'yes', 'no', 'no'),
  /*
   * ================================================================================================
   * INNSTILLINGEN ATLE BA OM: «HVILKE ROLLER FÅR AUTOMATISK TILGANG TIL ALLE FESTIVALER?» (sak 182)
   * ================================================================================================
   *
   * Atle, 21. september: «Innstilling under Rettigheter: Hvilke roller får automatisk tilgang til
   * alle festivaler og venues (alle årganger, også framtidige)? … Roller under nivået ser bare
   * festivaler/årganger de er lagt til i.»
   *
   * EN EGEN BRYTER, OG IKKE EN NY VERDI I `festival:list`. Jeg bygde det først som en ny celle —
   * `assigned` ved siden av `yes` — og måtte rive det igjen da jeg målte hva basen faktisk lagrer:
   *
   *     generate-default-permissions.ts:  allowed: cell !== 'no'
   *
   * `role_permissions` har ÉN redigerbar kolonne, og den er boolsk. Hver nyanse — `own`, `dept`,
   * `part`, `grant` — kommer ut av generatoren som `allowed = true` og får betydningen sin av en
   * regel i kode. En ny celleverdi ville derfor vært en innstilling som IKKE kan endres per hus:
   * det motsatte av det Atle ba om, og et brudd på prinsipp 9. Ingenting hadde blitt rødt av det.
   *
   * En egen handling har ingen av de problemene. `allowed` er hele svaret — «ja, denne rollen får
   * alt» eller «nei, bare det hen er lagt til i» — så innstillingen er en rad per rolle i en tabell
   * som allerede er per hus, allerede redigerbar under Rettigheter og allerede dekket av RLS.
   *
   * DEN GJELDER OGSÅ ÅRGANGER OG VENUES, ikke bare festivaler. Én bryter, ikke tre: Atle skrev dem
   * som ett spørsmål («alle festivaler og venues, alle årganger»), og en årgang man ikke kan se
   * festivalen til er ikke en tilgang noen har bruk for.
   *
   * ------------------------------------------------------------------------------------------------
   * STANDARDEN BEHOLDER DAGENS OPPFØRSEL, MOT ORDLYDEN I ORDREN. Her er hvorfor.
   *
   * Atle skrev to ting som ikke kan være sanne samtidig: «Standard: Medarbeider og over» OG «ingen
   * mister tilgang de har i dag». Målt rett over: `limited` — «Begrenset» — står på `yes` for
   * `festival:list` og har full festivaltilgang i dag. Settes standarden til medarbeider-og-over,
   * mister hver eneste begrensede bruker i hvert eneste hus tilgangen sin i det migreringen kjører.
   *
   * Av de to valgte jeg den som ikke kan ødelegge noe. `external` står på `no` fordi en ekstern
   * ALDRI har hatt automatisk tilgang — der er de to setningene enige. Sak 182 har spørsmålet, med
   * den ene formen jeg ser som gir ham begge: ny standard for NYE hus, uendret for de som finnes.
   */
  'festival:access_all': cells('yes', 'yes', 'yes', 'yes'),

  // ---------- editions ----------
  'edition:list': cells('yes', 'yes', 'yes', 'yes'),
  'edition:read': cells('yes', 'yes', 'yes', 'yes'),
  'edition:create': cells('yes', 'yes', 'no', 'no'),
  'edition:update': cells('yes', 'yes', 'no', 'no'),
  'edition:archive': cells('yes', 'yes', 'no', 'no'),
  'edition:delete': cells('yes', 'no', 'no', 'no'),
  'edition:share': cells('yes', 'yes', 'no', 'no'),
  /*
   * F13 (Atle 04.10): å dele årgangens kart og område — porten, strømmen og vannet — med foodtrucker,
   * vakter og leverandører. Samme standard som å endre kartet (`edition:update`).
   */
  'map:share': cells('yes', 'yes', 'no', 'no'),

  /*
   * ---------- economy (sak 53) ----------
   *
   * THE DEFAULT IS NARROW AND THE CELL IS ADJUSTABLE, which is exactly what Atle asked for: "Ha
   * dette som et option i innstillinger på rettigheter. Slike ting skal helst ikke hardkodes."
   *
   * Superadmin and admin by default, because of what is behind it: `PAGES.md` puts every cost line
   * on that page, and vedtak 0008 routes artist fees there ON PURPOSE — away from the contact
   * register precisely because every `member` reads that register in full. A default that opened
   * fees to everybody could not be un-opened.
   *
   * But a festival whose production manager owns the budget will want it wider, and that is the
   * organisation's call rather than ours — which is what `ADJUSTABLE_ACTIONS` is for. The route
   * that used to guard on `edition:update` now asks this instead, so switching the cell in
   * Settings really moves the door.
   */
  'economy:read': cells('yes', 'yes', 'no', 'no'),
  /*
   * WRITING THE MONEY, and the three cells match the door rather than being narrower than it.
   *
   * A cost line is entered where it arises — a fee on a booking, an invoice on the year — so the
   * people who enter them are the people who already read the page. Making writes narrower than
   * `economy:read` would produce the worst of both: a production manager who can see the budget
   * being overrun and cannot record the line that overran it.
   *
   * Adjustable since 0475 (Atle 30.09, «Føre økonomien»), as ONE setting apart from `economy:read`:
   * the default still matches the door, but seeing and writing can now be answered separately.
   */
  'economy:create': cells('yes', 'yes', 'no', 'no'),
  'economy:update': cells('yes', 'yes', 'no', 'no'),
  'economy:delete': cells('yes', 'yes', 'no', 'no'),

  /*
   * ---------- kontraktfanen på en programpost (PERFORMANCE.md) ----------
   *
   * The same shape as economy and for the same reason: this is where the fee lives, and vedtak
   * 0008 routes fees away from the contact register ON PURPOSE because every `member` reads that
   * register in full. `external` is `no` and stays `no` — «deles aldri med eksterne» is the
   * plan's own words, and the Deling tab draws that row locked.
   *
   * `contract:read` IS ADJUSTABLE, exactly like `economy:read` (vedtak 0021): a festival whose
   * production manager negotiates answers this differently from one where the office does. The
   * WRITE is its own setting since 0475 («Endre kontrakter»), with the same default.
   */
  'contract:read': cells('yes', 'yes', 'yes', 'no'),
  'contract:update': cells('yes', 'yes', 'no', 'no'),
  /*
   * DEN FELLES KONTRAKTEN (sak 274, C4–C8). Å lage og fylle ut er `draft`; å signere er en fullmakt
   * gitt til en person (`signing_authorities`), ikke en celle. `register` er å føre inn en kontrakt
   * signert utenfor Tvitvi, og `void` å annullere. `manage_roles` er å gi fullmakt: bare superadmin.
   */
  'contract:draft': cells('yes', 'yes', 'yes', 'no'),
  'contract:register': cells('yes', 'yes', 'no', 'no'),
  'contract:void': cells('yes', 'no', 'no', 'no'),
  'contract:manage_roles': cells('yes', 'no', 'no', 'no'),

  /*
   * ---------- avtalen med artisten (ARTIST-AVTALE-ORDREN Del 1) ----------
   *
   * Atles standard: «byråets admin+ leser og skriver, member leser, limited/external ingen.»
   *
   * MEMBER LESER OG SKRIVER IKKE, og det er den ene cella som er verdt en setning. En bookingagent
   * er `member` i byrået og trenger å VITE hva som er avtalt — kan artisten spille i Sverige, hva
   * er minstehonoraret — for å svare på en forespørsel i det hele tatt. Men provisjonssatsen er
   * husets forhold til artisten, ikke agentens å endre.
   *
   * `external` er `no` og ikke `grant`: en avtale har ingen delingsvei å skru på. Se `types.ts`.
   */
  'artist_agreement:read': cells('yes', 'yes', 'yes', 'no'),
  'artist_agreement:update': cells('yes', 'yes', 'no', 'no'),
  // Sak 216.14: «egen celle i Rettigheter, med medarbeider som standard».
  'artist:invite': cells('yes', 'yes', 'yes', 'no'),
  // Sak 215.29: hvem som arkiverer en artist fra stallen. Atle satte ingen standard; admin og opp.
  'artist:archive': cells('yes', 'yes', 'no', 'no'),
  // «Overganger» (28. september): samme standard som å se registeret, men justerbar.
  'agency_move:list': cells('yes', 'yes', 'yes', 'yes'),
  // F21 (pakke 3): varsel om bud og forespørsler ingen agent er ansvarlig for. «Alle med bookingrett»
  // er å slå den på for admin og medarbeider.
  'booking_enquiry:notify': cells('yes', 'no', 'no', 'no'),

  /*
   * ---------- Booking og Artister, egne linjer (G2, QA13 #87, 0923) ----------
   *
   * Standarden er dagens: hver celle har samme verdi som `performance:*`-cella den erstatter (i
   * kommentaren), og 0923 kopierte verdien per organisasjon og rolle. «Se priser og honorar» var
   * `contract:read` ELLER `performance:update` (253.63: standard Medarbeider, som i dag).
   */
  'booking:list': cells('yes', 'yes', 'yes', 'yes'), //                 performance:list
  'booking_artist:create': cells('yes', 'yes', 'yes', 'no'), //        performance:update
  'booking_artist:update': cells('yes', 'yes', 'yes', 'no'), //        performance:update
  'booking_draft:create': cells('yes', 'yes', 'yes', 'no'), //         performance:create
  'booking_draft:update': cells('yes', 'yes', 'yes', 'no'), //         performance:update
  'booking_enquiry:create': cells('yes', 'yes', 'yes', 'no'), //       performance:update
  'booking_bid:create': cells('yes', 'yes', 'yes', 'no'), //           performance:create
  'booking_bid:update': cells('yes', 'yes', 'yes', 'no'), //           performance:update
  'booking_bid:respond': cells('yes', 'yes', 'yes', 'no'), //          performance:respond
  'booking_amount:read': cells('yes', 'yes', 'yes', 'no'), //          contract:read | performance:update
  'booking:delete': cells('yes', 'yes', 'no', 'no'), //                performance:delete
  /* Sak 237.2 «Slette artister i booking», før `artist:delete` (som lå under Artister i den faste tabellen). */
  'booking_artist:delete': cells('yes', 'yes', 'no', 'no'), //         artist:delete
  'artist:list': cells('yes', 'yes', 'yes', 'yes'), //                  performance:list
  'artist:update': cells('yes', 'yes', 'yes', 'no'), //                 performance:update
  'agency_booking:create': cells('yes', 'yes', 'yes', 'no'), //        performance:create
  'agency_booking:update': cells('yes', 'yes', 'yes', 'no'), //        performance:update
  'agency_booking:respond': cells('yes', 'yes', 'yes', 'no'), //       performance:update

  // ---------- spillesteder og konserter (27. september, SPILLESTED-KONSERT-PLAN.md) ----------
  // «Tilgang hardkodes aldri»: hver ny handling er en celle, og de som huset kan være uenig om
  // står i ADJUSTABLE_ACTIONS under. Leien og budsjettet er admin og opp, som `economy:read`.
  'venue:list': cells('yes', 'yes', 'yes', 'yes'),
  'venue:read': cells('yes', 'yes', 'yes', 'yes'),
  'venue:create': cells('yes', 'yes', 'no', 'no'),
  'venue:update': cells('yes', 'yes', 'no', 'no'),
  'venue:delete': cells('yes', 'yes', 'no', 'no'),
  // F18 (runde 4. oktober): dele salens spesifikasjon med en ekstern. Som å endre salen.
  'venue:share': cells('yes', 'yes', 'no', 'no'),
  // QA16 301.10: synligheten og den publiserte kalenderen på salen. Som å endre salen i dag.
  'venue:publish': cells('yes', 'yes', 'no', 'no'),
  'venue_rate:read': cells('yes', 'yes', 'no', 'no'),
  'venue_rate:update': cells('yes', 'yes', 'no', 'no'),
  'venue_enquiry:list': cells('yes', 'yes', 'yes', 'no'),
  'venue_enquiry:read': cells('yes', 'yes', 'yes', 'no'),
  'venue_enquiry:respond': cells('yes', 'yes', 'no', 'no'),
  'venue_night:list': cells('yes', 'yes', 'yes', 'yes'),
  'venue_night:read': cells('yes', 'yes', 'yes', 'yes'),
  'venue_night:create': cells('yes', 'yes', 'yes', 'no'),
  'venue_night:update': cells('yes', 'yes', 'yes', 'no'),
  'venue_night:delete': cells('yes', 'yes', 'no', 'no'),
  // F18: dele arrangementet (dato, tider, get-in, adresse). Som å endre det.
  'venue_night:share': cells('yes', 'yes', 'yes', 'no'),
  'venue_registry:list': cells('yes', 'yes', 'yes', 'no'),
  'venue_registry:create': cells('yes', 'yes', 'yes', 'no'),
  'venue_registry:update': cells('yes', 'yes', 'yes', 'no'),
  'venue_registry:delete': cells('yes', 'yes', 'no', 'no'),
  'date_request:list': cells('yes', 'yes', 'yes', 'no'),
  'date_request:create': cells('yes', 'yes', 'yes', 'no'),
  // Trekke tilbake og skrive i samtalen om en dato (agent C, 28.09).
  'date_request:update': cells('yes', 'yes', 'yes', 'no'),
  'concert:list': cells('yes', 'yes', 'yes', 'yes'),
  'concert:read': cells('yes', 'yes', 'yes', 'yes'),
  'concert:create': cells('yes', 'yes', 'yes', 'no'),
  'concert:update': cells('yes', 'yes', 'yes', 'no'),
  'concert:delete': cells('yes', 'yes', 'no', 'no'),
  'concert_budget:read': cells('yes', 'yes', 'no', 'no'),
  'concert_budget:update': cells('yes', 'yes', 'no', 'no'),
  'concert_template:list': cells('yes', 'yes', 'yes', 'no'),
  'concert_template:create': cells('yes', 'yes', 'no', 'no'),
  'concert_template:update': cells('yes', 'yes', 'no', 'no'),
  'concert_template:delete': cells('yes', 'yes', 'no', 'no'),

  /*
   * ---------- performances ----------
   *
   * MEMBER WRITES HERE, AND THAT IS THE DELIBERATE DIFFERENCE FROM EDITIONS ABOVE.
   *
   * An edition is a structural decision — a festival adds a year rarely and on purpose, so
   * `edition:create` stops at admin. A performance is daily work: the booking coordinator in the
   * seed is Mia Moen, a `member`, and a booker who cannot record that an agent replied is a
   * booker who keeps the line-up in a spreadsheet.
   *
   * There is no `performance:set_status` cell. Decision 0011 makes the booking chain an ordered
   * status with free movement, so moving it IS an update — and a separate cell would be a second
   * rule saying the same thing, which is a rule that eventually disagrees with the first.
   *
   * `grant` for external on list and read: an agency or a supplier is shown the one performance
   * they are working on, through `object_grants`, which is exactly what that dimension is for.
   * Deleting and sharing stay with admin — sharing is how an external gets in at all, and a
   * decision that widens access is not daily work.
   */
  'performance:list': cells('yes', 'yes', 'yes', 'yes'),
  'performance:read': cells('yes', 'yes', 'yes', 'yes'),
  'performance:create': cells('yes', 'yes', 'yes', 'no'),
  'performance:update': cells('yes', 'yes', 'yes', 'no'),
  /*
   * Å SKRIVE I BOOKINGENS CHAT. Samme tre roller som fører bookingen, av samme grunn: en
   * samtale om en konsert er en del av å arbeide med den. `limited` leser programmet uten å
   * forhandle i det, og en ekstern med en delt lenke skal aldri kunne skrive inn i huset.
   */
  'performance:comment': cells('yes', 'yes', 'yes', 'no'),
  'performance:delete': cells('yes', 'yes', 'no', 'no'),
  /*
   * F22 (runde 4. oktober): «Godta motbudet» og «Gi nytt bud». Å si ja til byråets tall binder
   * huset til et honorar, så det er en egen celle — med de tre rollene som fører bookingen som
   * standard, slik som `performance:update`.
   */
  'performance:respond': cells('yes', 'yes', 'yes', 'no'),
  'performance:share': cells('yes', 'yes', 'no', 'no'),

  /**
   * ---------- stages ----------
   *
   * A stage is STRUCTURE, like a department, and it is read by everybody and written by few.
   * Atle, 29 August, answer A = 3: the festival keeps a standard set and a year may override it,
   * so the same four cells govern both levels — a person who may edit the festival's set may edit
   * a year's, because the harder of the two acts is changing every year at once.
   *
   * `external` may NOT list, and the first draft of this said the opposite. The argument for `yes`
   * was that a supplier looking at the one performance they were granted must be able to see which
   * stage it is on, or the row is a booking with no place. True — and already satisfied without
   * this cell: `performanceDtoSchema` carries `stage` inline, resolved in the same statement as the
   * row. What `stage:list` would ADD is the ability to enumerate every stage of every festival in
   * the organisation, including festivals `festival:list` refuses them (`grant` for external) and
   * years `edition:list` refuses them. Stage names and capacities are a map of what the
   * organisation runs and how big it is, handed to somebody granted one booking.
   *
   * So it is `no`, and the supplier loses nothing. Every other list an external can reach is
   * `grant`-scoped; a plain `yes` here would have been the one row in the matrix that was not.
   *
   * `limited` may not create or edit. A stage is the festival's shape, not a piece of daily work,
   * and a crew member adding "Scene 2" because they could not find "Scene II" is how a line-up
   * ends up with two of everything.
   *
   * There is deliberately no `stage:read`: a cell the Settings panel offers and nothing consumes.
   *
   * `stage:share` came with F14 (4 October): the running order for one stage and one day as a link
   * («lydfirmaet trenger Teltet fredag»). Same default as `performance:share`, because it shares
   * the programme the stage carries.
   */
  'stage:list': cells('yes', 'yes', 'yes', 'yes'),
  'stage:create': cells('yes', 'yes', 'yes', 'no'),
  'stage:update': cells('yes', 'yes', 'yes', 'no'),
  'stage:delete': cells('yes', 'yes', 'no', 'no'),
  'stage:share': cells('yes', 'yes', 'no', 'no'),

  /*
   * ---------- accreditation levels (Settings [F2]) ----------
   *
   * Read by everyone who works the festival, because People shows a person's level and a crew
   * list that says nothing about badges is a crew list nobody can use at the gate. Written by
   * admin and above only: a badge level decides who gets backstage, which is the one thing on the
   * Settings page with a physical consequence.
   *
   * `external` is `no` even on list. An agency has no business enumerating the site's zones.
   * Four actions, four routes — the same restraint stages showed above.
   */
  'accreditation_level:list': cells('yes', 'yes', 'yes', 'yes'),
  'accreditation_level:create': cells('yes', 'yes', 'no', 'no'),
  'accreditation_level:update': cells('yes', 'yes', 'no', 'no'),
  'accreditation_level:delete': cells('yes', 'yes', 'no', 'no'),

  /*
   * ---------- ressurslaget (spesifikasjonen §4, trinn 1) ----------
   *
   * TRE NIVÅER, OG DE STIGER FRA VENSTRE MOT HØYRE I HVERT SITT TEMPO.
   *
   * `resource_type:*` er Innstillinger: å definere en type er å bestemme hva festivalen holder
   * styr på i det hele tatt. Det er den samme handlingen som å legge til en avdeling eller et
   * akkrediteringsnivå, og den ligger der de ligger — admin og over. En `member` som fant på en
   * type til er hvordan et register ender med «Bil», «Biler» og «Kjøretøy».
   *
   * `resource:*` er registerarbeid. En `member` skal kunne legge inn Bil 3 når den ankommer, og
   * det er ikke den samme avgjørelsen som å finne opp kategorien den hører til. Sletting er
   * likevel admin: en ressurs bærer tildelinger, og `on delete restrict` gjør at feilen dukker opp
   * som en skrankefeil framfor som et tap — men den som rydder skal vite hva de rydder.
   *
   * `allocation:*` er DAGLIG PRODUKSJON, og det er her matrisen skiller seg fra de to over. Den
   * som klokka 14 oppdager at bussen må hente en time tidligere skal kunne flytte den selv.
   * Samme resonnement som skift: «a plan only an admin may change is a plan that gets kept in a
   * spreadsheet.» `limited` leser sitt eget året gjennom `allocation:list`, men skriver ikke.
   *
   * `external` ser ingenting her ennå. Byråets vei inn er den delte programposten, og den kommer i
   * trinn 2 (spesifikasjonen §6, «Synlighet for byrået»). En `yes` nå ville åpnet hele bilparken
   * framfor den ene bilen som henter dem.
   *
   * `allocation:update` DEKKER «AVKLAR». Å kvittere for en konflikt er å skrive på tildelingen, og
   * en egen celle for det ville vært en celle Innstillinger tilbyr og ingen forstår.
   */
  'resource_type:list': cells('yes', 'yes', 'yes', 'yes'),
  'resource_type:create': cells('yes', 'yes', 'no', 'no'),
  'resource_type:update': cells('yes', 'yes', 'no', 'no'),
  'resource_type:delete': cells('yes', 'yes', 'no', 'no'),

  'resource:list': cells('yes', 'yes', 'yes', 'yes'),
  'resource:create': cells('yes', 'yes', 'yes', 'no'),
  /* 'dept' for limited siden 0357: avdelingslederen styrer sin egen avdelings ressurser (201.15). */
  'resource:update': cells('yes', 'yes', 'yes', 'dept'),
  'resource:delete': cells('yes', 'yes', 'no', 'no'),
  /* Sak 232: å dele en ressurs med en person utenfor organisasjonen. Begrenset: egen avdeling. */
  'resource:share': cells('yes', 'yes', 'yes', 'dept'),

  'allocation:list': cells('yes', 'yes', 'yes', 'yes'),
  'allocation:create': cells('yes', 'yes', 'yes', 'dept'),
  'allocation:update': cells('yes', 'yes', 'yes', 'dept'),
  'allocation:delete': cells('yes', 'yes', 'yes', 'dept'),

  /*
   * ---------- deltakelseslaget (trinn 3) ----------
   *
   * FELTENE OG ROLLENE ER INNSTILLINGER, DELTAKELSEN ER ARBEID — samme deling som ressurslaget
   * over, og den er den samme setningen: å bestemme hva festivalen spør om er et valg for huset;
   * å føre et menneske inn i årgangen gjør produksjonen hver dag.
   *
   * `limited` LESER SIN EGEN RAD, og det er `own` og ikke `yes`. En frivillig skal kunne se at de
   * står oppført, med riktig nivå og riktig avdeling — og de skal ikke kunne bla i hele staben.
   * Det er nøyaktig samme innsnevring `crew_member:list` har hatt siden fase 0, og de to leser den
   * samme lista.
   *
   * `external` SER INGENTING, og det er verdt å si høyt fordi trinn 3 handler om byråets folk:
   * byrået skal se SINE mennesker gjennom den delte programposten og advancens People-fane, ikke
   * gjennom festivalens Personer-side. En `yes` her ville gitt dem hele stab- og frivilliglista.
   */
  'person_field:list': cells('yes', 'yes', 'yes', 'yes'),
  'person_field:create': cells('yes', 'yes', 'no', 'no'),
  'person_field:update': cells('yes', 'yes', 'no', 'no'),
  'person_field:delete': cells('yes', 'yes', 'no', 'no'),

  'participation_role:list': cells('yes', 'yes', 'yes', 'yes'),
  'participation_role:create': cells('yes', 'yes', 'no', 'no'),
  'participation_role:update': cells('yes', 'yes', 'no', 'no'),
  'participation_role:delete': cells('yes', 'yes', 'no', 'no'),

  'participation:list': cells('yes', 'yes', 'yes', 'own'),
  'participation:create': cells('yes', 'yes', 'yes', 'no'),
  'participation:update': cells('yes', 'yes', 'yes', 'no'),
  'participation:delete': cells('yes', 'yes', 'no', 'no'),

  /*
   * ---------- People [F2]: bemanning and shifts ----------
   *
   * A MEMBER WRITES HERE, unlike badge levels above. Staffing a year and planning shifts is daily
   * production work — the person who discovers at 14:00 that the gate needs two more is the
   * production coordinator, not an admin — and a plan only an admin may change is a plan that gets
   * kept in a spreadsheet.
   *
   * `limited` reads only their OWN row and their own shifts: a volunteer needs to know when they
   * are on, and does not need the whole crew list. `external` gets nothing at all here; an agency
   * has no business enumerating who works the festival.
   */
  /*
   * F34 (runde 4. oktober, sak 66): masseutsending — beskjed, vaktplan og vaktinfo på e-post til
   * frivillige og personer i en festival eller et spillested, og i Innboks for dem med innlogging.
   * Å sende og å se hva som er sendt er ett valg. Standard admin og opp: gratisplanen hos Brevo har
   * en dagsgrense for hele plattformen, og én utsending kan bruke den opp.
   */
  'broadcast:create': cells('yes', 'yes', 'no', 'no'),
  'broadcast:list': cells('yes', 'yes', 'no', 'no'),
  'crew_member:list': cells('yes', 'yes', 'yes', 'own'),
  'crew_member:create': cells('yes', 'yes', 'yes', 'no'),
  'crew_member:update': cells('yes', 'yes', 'yes', 'no'),
  'crew_member:delete': cells('yes', 'yes', 'yes', 'no'),
  'shift:list': cells('yes', 'yes', 'yes', 'own'),
  /*
   * AVDELINGSLEDEREN (201.15, 0357). `limited` går fra `no` til `dept`: en frivillig som leder en
   * avdeling setter opp og bemanner vaktene der, og ingen andre steder. En frivillig uten avdeling
   * har ingen avdelinger å matche, og er dermed like avvist som før.
   */
  'shift:create': cells('yes', 'yes', 'yes', 'dept'),
  'shift:update': cells('yes', 'yes', 'yes', 'dept'),
  'shift:delete': cells('yes', 'yes', 'yes', 'dept'),
  /* «Bekreft» (og «Takk nei» når årgangen tillater det) på en vakt man selv står på (201.14). */
  'shift:respond': cells('yes', 'yes', 'yes', 'own'),
  /* Sak 232: å dele en vakt. Begrenset: egen avdeling, som vaktplanen ellers. */
  'shift:share': cells('yes', 'yes', 'yes', 'dept'),
  /*
   * PÅMELDING OG BYTTE (QA16 297.4, 1321). Å be om en ledig vakt eller et bytte gjelder bare en selv,
   * så alle roller kan. Å godkjenne er å bemanne vakta: samme standard som `shift:update`.
   */
  'shift:request': cells('yes', 'yes', 'yes', 'yes'),
  'shift:assign': cells('yes', 'yes', 'yes', 'dept'),

  /*
   * ---------- People [F2]: frivillige og påmeldingsskjemaer ----------
   *
   * NARROWER THAN THE SHIFT PLAN ABOVE, and deliberately so. PEOPLE.md's sixth principle is
   * «personopplysninger varsomt»: a signup carries a phone number, a date of birth, free-text
   * answers and a consent timestamp, which is a different kind of row from "the gate needs two
   * more people". A member reads and processes them — that IS the volunteer coordinator's daily
   * work — and DELETING one is an admin act, because removing a person's record is not something
   * to do while clearing a list.
   *
   * A FORM IS NARROWER STILL. It is a page on the open internet under the festival's name, which
   * anybody with the link may write to. Publishing one is a decision about the organisation, not
   * about this week's staffing, so `member` reads the forms and does not make them.
   *
   * `limited` and `external` get nothing at all: a volunteer has no business enumerating the
   * other applicants, and an agency has no business seeing a recruitment.
   */
  'volunteer:list': cells('yes', 'yes', 'yes', 'no'),
  'volunteer:read': cells('yes', 'yes', 'yes', 'no'),
  'volunteer:create': cells('yes', 'yes', 'yes', 'no'),
  'volunteer:update': cells('yes', 'yes', 'yes', 'no'),
  'volunteer:delete': cells('yes', 'yes', 'no', 'no'),
  'signup_form:list': cells('yes', 'yes', 'yes', 'no'),
  'signup_form:create': cells('yes', 'yes', 'no', 'no'),
  'signup_form:update': cells('yes', 'yes', 'no', 'no'),
  'signup_form:delete': cells('yes', 'yes', 'no', 'no'),

  /*
   * ---------- People [F2]: akkreditering ----------
   *
   * WIDER THAN THE VOLUNTEER LIST, and that is the accreditation desk being a desk. Handing out a
   * card happens at a table by the gate, at eleven at night, staffed by whoever is on that shift —
   * so a `member` reads the list and marks cards out. Adding a press pass is the same person's
   * job at the same table.
   *
   * DELETING stops at admin, like the volunteer list: taking somebody's accreditation off the
   * list is not something to do while working through a queue of arrivals.
   *
   * `limited` reads NOTHING here, and that is deliberate even though a volunteer might like to
   * check their own level: the list is every name and every zone at the festival, which is the one
   * document a stolen laptop should not open.
   */
  'accreditation:list': cells('yes', 'yes', 'yes', 'no'),
  'accreditation:create': cells('yes', 'yes', 'yes', 'no'),
  'accreditation:update': cells('yes', 'yes', 'yes', 'no'),
  'accreditation:delete': cells('yes', 'yes', 'no', 'no'),

  /*
   * ---------- Documents [F2] and Documents (arrangement) [F2] ----------
   *
   * READING IS `dept`, AND THAT SCOPE IS THE PER-FOLDER ACCESS THE PAGE ASKS FOR.
   *
   * «Tilgangsstyring per mappe basert på rettigheter.» A folder with no department is open to
   * everybody who may read documents at all; a folder that names one is that department's. The
   * `dept` cell is what turns those two sentences into a WHERE clause, so nothing here is a second
   * mechanism — it is the same scope machinery Contacts and People already run on.
   *
   * `limited` therefore reads the open folders and their own departments' — a volunteer needs the
   * evacuation plan and has no business in the contracts folder.
   *
   * `external` is `grant`: `document` is in `object_type`, so a single file can be shared with an
   * agency the day sharing is built. Until then the grant list is empty, the scope is false, and an
   * external sees no documents at all — which is the right default to arrive at by accident.
   *
   * A MEMBER UPLOADS AND A MEMBER DELETES THEIR OWN. Production runs on somebody attaching the
   * signed contract at 23:00; a shelf only an admin may fill is a shelf that stays empty while the
   * file goes out by email. Deleting is `own` for exactly the opposite reason: taking a file off
   * the shelf that somebody else put there is not daily work.
   */
  'document:list': cells('yes', 'yes', 'yes', 'dept'),
  'document:read': cells('yes', 'yes', 'yes', 'dept'),
  'document:create': cells('yes', 'yes', 'yes', 'no'),
  'document:delete': cells('yes', 'yes', 'yes', 'own'),
  /*
   * F12 (Atle 04.10): å dele et dokument med en ekstern eller som lenke. Som opplastingen: hele
   * produksjonen sender rideren og HMS-planen, Begrenset gjør det ikke som standard. Kontrakt- og
   * økonomimapper deles aldri, uansett celle — det er basen som nekter.
   */
  'document:share': cells('yes', 'yes', 'yes', 'no'),
  /*
   * THE STRUCTURE IS ADMIN WORK. A folder tree everybody may rearrange is a tree where nobody can
   * find last year's insurance, and the template that furnishes a new årgang loses its meaning the
   * first time somebody renames «HMS» to «diverse».
   */
  'folder:create': cells('yes', 'yes', 'no', 'no'),
  'folder:update': cells('yes', 'yes', 'no', 'no'),
  'folder:delete': cells('yes', 'yes', 'no', 'no'),
  /* F12: å dele en hel mappe (med undermappene) er hylla og ikke én fil — samme standard som strukturen. */
  'folder:share': cells('yes', 'yes', 'no', 'no'),

  // ---------- tasks ----------
  'task:list': cells('yes', 'yes', 'yes', 'own'),
  'task:read': cells('yes', 'yes', 'yes', 'own'),
  /*
   * `limited` CAN CREATE TASKS. Atle, 28 August, answering question 3: "Ja, det tenker jeg. Kan
   * legge til andre i organisasjonen."
   *
   * Open since Phase 0, marked "decide before the Tasks page is built", and the page was built
   * against the old answer. Nothing has to change there: `TasksPage` asks `can()` rather than
   * checking a role, so the button appears on its own.
   *
   * Note what does NOT move with it. `task:assign` stays `no` for `limited` — creating work and
   * handing work to a named person are different powers, and Atle's sentence is about the first.
   * `task:delete` and `task:list` are untouched too, so a limited user can make a task and then
   * see only their own.
   */
  'task:create': cells('yes', 'yes', 'yes', 'yes'),
  'task:update': cells('yes', 'yes', 'yes', 'own'),
  'task:assign': cells('yes', 'yes', 'yes', 'no'),
  /*
   * COMMENTING IS ITS OWN CELL (sak 56, answered 31 August: «slik hører hjemme i innstillinger»).
   *
   * The default is exactly what the route enforced before it existed — whoever may change the task
   * may say something about it — so switching this on changes nothing until an organisation asks
   * for it. What it makes possible is the case that raised the question: an `external` with a view
   * grant answering «ja, bussen er bestilt» on their own show, without being able to move the
   * deadline. That is `grant` here and `no` on `task:update`, and it is now one switch away.
   */
  'task:comment': cells('yes', 'yes', 'yes', 'own'),
  'task:complete': cells('yes', 'yes', 'yes', 'own'),
  'task:delete': cells('yes', 'yes', 'own', 'own'),
  'task:restore': cells('yes', 'yes', 'no', 'no'),
  'task:share': cells('yes', 'yes', 'no', 'no'),

  // ---------- kalender ----------
  /*
   * STANDARD = `task:*`, CELLE FOR CELLE (Atles avgjørelse 17. september).
   *
   * Det er standarden som gjør endringen trygg: ingen ser noe annet enn i går før noen faktisk
   * flytter en bryter i Innstillinger.
   *
   * `external` er `grant` her som der, og det er verdt en setning fordi et grant aldri peker på
   * et MØTE: kalenderen tegner også oppgaver, programposter og skift, og hver av de radene snevres
   * av sin egen celle. Cella her åpner SIDEN — og en ekstern som mistet den, mistet den eneste
   * flata som viser dem når deres egen konsert er.
   */
  'event:list': cells('yes', 'yes', 'yes', 'own'),
  'event:read': cells('yes', 'yes', 'yes', 'own'),
  'event:create': cells('yes', 'yes', 'yes', 'yes'),
  'event:update': cells('yes', 'yes', 'yes', 'own'),
  'event:delete': cells('yes', 'yes', 'own', 'no'),

  // ---------- inbox ----------
  /*
   * NOBODY READS A THREAD THEY ARE NOT IN. NOT ADMIN, NOT SUPERADMIN.
   *
   * Atle, 28 August, answering question 1: "nei, ikke superadmin heller. De må bli lagt til i
   * gruppen." Stricter than the option he was offered, which only narrowed `admin`.
   *
   * This row is the reason the question existed. Three layers disagreed: the database said yes
   * for the top three roles, this matrix said the same, and the SERVICE said no — because
   * `ThreadsService` joins through `thread_participants` on every read. The strictest layer won,
   * so nothing ever leaked, but it was safe because of the shape of one SQL statement rather than
   * because a rule said so. Eight tests pinned that, mutation-tested.
   *
   * Now the rule says so. `part` for all five means the matrix finally states the behaviour the
   * code already had, and a future query written without the participation join would be refused
   * by `can()` instead of quietly returning somebody else's correspondence.
   *
   * How a person GETS added to a thread when an artist is attached to a festival is a separate
   * decision Atle is taking later.
   */
  'thread:list': cells('part', 'part', 'part', 'part'),
  'thread:read': cells('part', 'part', 'part', 'part'),
  'thread:create': cells('yes', 'yes', 'yes', 'no'),
  'thread:update': cells('yes', 'yes', 'yes', 'no'),
  'thread:delete': cells('yes', 'yes', 'no', 'no'),
  'message:list': cells('part', 'part', 'part', 'part'),
  'message:create': cells('yes', 'yes', 'yes', 'part'),
  'message:update': cells('own', 'own', 'own', 'own'),
  'message:delete': cells('yes', 'yes', 'no', 'no'),

  // ---------- taxonomy (principle 9: this is data, not code) ----------
  'tag:list': cells('yes', 'yes', 'yes', 'yes'),
  'tag:create': cells('yes', 'yes', 'yes', 'no'),
  'tag:update': cells('yes', 'yes', 'yes', 'no'),
  'tag:delete': cells('yes', 'yes', 'no', 'no'),
  'department:list': cells('yes', 'yes', 'yes', 'yes'),
  'department:read': cells('yes', 'yes', 'yes', 'yes'),
  'department:create': cells('yes', 'yes', 'no', 'no'),
  'department:update': cells('yes', 'yes', 'no', 'no'),
  'department:delete': cells('yes', 'yes', 'no', 'no'),
  /* Sak 232: å dele en hel avdeling — vaktplan, ressurser, personer og dokumenter. */
  'department:share': cells('yes', 'yes', 'no', 'no'),

  // ---------- notifications (always self-scoped; RLS agrees) ----------
  'notification:list': cells('own', 'own', 'own', 'own'),
  'notification:read': cells('own', 'own', 'own', 'own'),
  'notification:update': cells('own', 'own', 'own', 'own'),

  // ---------- utkast (D9: alltid forfatterens egne; RLS sier det samme) ----------
  'draft:list': cells('own', 'own', 'own', 'own'),
  'draft:create': cells('yes', 'yes', 'yes', 'yes'),
  'draft:delete': cells('own', 'own', 'own', 'own'),
  'draft:send': cells('own', 'own', 'own', 'own'),

  // ---------- sharing (access dimension 4) ----------
  'object_grant:list': cells('yes', 'yes', 'no', 'no'),
  'object_grant:create': cells('yes', 'yes', 'no', 'no'),
  'object_grant:delete': cells('yes', 'yes', 'no', 'no'),
  // Eksport (docs/EKSPORT.md §6): «Standard: på for alle roller.» Innholdet følger de andre
  // cellene og RLS; dette er bare døra.
  'export:create': cells('yes', 'yes', 'yes', 'yes'),
  /*
   * DELINGENE (sak 232, godkjent 29.09 med disse standardene). Å invitere en ekstern (personlig
   * konto), å lage en lenke til en lenkeperson, og å dele med nivået «kan endre». Bare det
   * organisasjonen selv er part i kan deles — det er en regel i `rules.ts`, ikke en celle.
   */
  'external:invite': cells('yes', 'yes', 'yes', 'no'),
  'share_link:create': cells('yes', 'yes', 'yes', 'no'),
  'share:edit_level': cells('yes', 'yes', 'no', 'no'),
  /*
   * F9 (Atle 04.10): oversikten «Delinger» under Innstillinger — se, forlenge og trekke tilbake alle
   * delingene organisasjonen har. Standard superadmin; «bør kunne velge hvilke nivå».
   */
  'share:list': cells('yes', 'no', 'no', 'no'),
  /*
   * 239.37 (Atle 04.10): om rollen ser en advance en ANNEN organisasjon har delt med oss. Begrenset
   * gjør det ikke som standard — den delte advancen bærer reisefølge, e-post og gjesteliste.
   */
  'shared_advance:read': cells('yes', 'yes', 'yes', 'no'),
  /*
   * F17 (Atle 04.10): dele en advance en annen organisasjon har delt med oss videre — til eksterne
   * med innlogging, aldri lenkepersoner, aldri økonomi eller kontrakt. Som å dele en programpost.
   * 244.9: i et byrå står den på for medarbeider (agentene fører advancen) — basen setter det (0802).
   */
  'shared_advance:share': cells('yes', 'yes', 'no', 'no'),

  // ---------- oversight ----------
  'audit_log:list': cells('yes', 'yes', 'no', 'no'),
  'audit_log:read': cells('yes', 'yes', 'no', 'no'),
  'consent:list': cells('yes', 'no', 'no', 'no'),

  // ---------- the matrix itself ----------
  'role_permission:list': cells('yes', 'no', 'no', 'no'),
  'role_permission:read': cells('yes', 'no', 'no', 'no'),
  /*
   * HVEM SOM KAN ENDRE RETTIGHETER ER EN CELLE (sak 239.38, Atle 04.10: «Bør ligge i innstillingene
   * for hvem som kan endre dem. Default er kun superadmin»). Låst til superadmin fram til 0540.
   * Superadmin mister den aldri (0474), og eskaleringsregelen står i `rules.ts`: den som ikke er
   * superadmin endrer bare cellene til roller under seg, og aldri cellene for Rettigheter selv.
   */
  'role_permission:update': cells('yes', 'no', 'no', 'no'),

  // ---------- self ----------
  'user:read': cells('own', 'own', 'own', 'own'),
  'user:update': cells('own', 'own', 'own', 'own'),
  'person:read': cells('yes', 'yes', 'yes', 'yes'),
  // `Partial`, because the catalogue is a subset of the 280 syntactically possible
  // `<resource>:<verb>` pairs — but every key still has to be one of them, and every value a
  // complete set of five cells.
} as const satisfies Partial<Readonly<Record<Action, RoleCells>>>;

/** The action catalogue as the template spells it — every action Tvitvi knows about. */
export type TemplateAction = keyof typeof DEFAULT_PERMISSION_MATRIX;

/** Every action Tvitvi knows about, in template order. */
export const TEMPLATE_ACTIONS = Object.keys(DEFAULT_PERMISSION_MATRIX) as readonly TemplateAction[];

export type LockReason = 'superadmin_only';

export interface LockedActionMeta {
  readonly reason: LockReason;
  /** i18n key for the Settings UI, present in both nb.json and en.json (principle 8). */
  readonly i18nKey: string;
}

const SUPERADMIN_ONLY: LockedActionMeta = {
  reason: 'superadmin_only',
  i18nKey: 'settings.permissions.locked.superadminOnly',
};

/**
 * The two actions that stay in code and can never be granted to another role, whatever
 * `role_permissions` says (§11.3.1). They protect against an organization configuring its way
 * around the security model:
 *
 * - `membership:manage_roles` — any role change to or from `superadmin`. If the matrix could
 *   hand this to `admin`, an `admin` could make themselves owner.
 * - `organization:delete` — it ends the customer relationship, the agreements and all content.
 *
 * `role_permission:update` was the third until 0540 (sak 239.38). It is a cell now, and what kept
 * it locked — «a role that can edit the matrix can grant itself everything else» — is answered by
 * `permissionChangeIsSafe` in `rules.ts` instead: below superadmin, only roles under one's own, and
 * never the cells of Rettigheter itself.
 *
 * The fourth protection, "at least one active superadmin", is not an action but a state, enforced
 * at commit by `app.protect_last_superadmin()` (decision 0002).
 *
 * These are **not** stored as rows: `app.reject_locked_permission()` refuses to store a row
 * naming any of them, for any role, so the database is a second, independent line of defence.
 */
export const LOCKED_ACTIONS: ReadonlyMap<Action, LockedActionMeta> = new Map<
  Action,
  LockedActionMeta
>([
  ['membership:manage_roles', SUPERADMIN_ONLY],
  ['organization:delete', SUPERADMIN_ONLY],
]);

export const isLockedAction = (action: Action): boolean => LOCKED_ACTIONS.has(action);

/**
 * LAYER 2 of the access model (byggeplan 1.1): the bounded, platform-defined list of permissions
 * an organisation's superadmin may switch per role in Settings.
 *
 * This is an ALLOW-list, and that direction is the whole point. The first implementation was a
 * deny-list — everything adjustable except three locked actions — which meant every new action
 * became customer-adjustable the moment it was added, unless someone remembered to lock it. That
 * is the kind of omission nobody notices until an organisation has switched on something it
 * should never have been offered.
 *
 * Inverted, the failure mode is harmless: forget to list an action and it simply stays at its
 * template value, which is the value we shipped and reviewed.
 *
 * The list is deliberately short. An entry belongs here only when real organisations genuinely
 * differ — not because a switch would be easy to build. Everything absent is fixed at the
 * template default; `role_permissions` still stores a row for it, but the Settings panel offers
 * no control and the database refuses the UPDATE.
 *
 * Adding an entry is a product decision, not a refactor. Record it in `docs/decisions/`.
 */
export const ADJUSTABLE_ACTIONS: ReadonlySet<Action> = new Set<Action>([
  // Does trusted permanent staff maintain the contact register, or only the office?
  // Organisations with freelance-heavy production teams answer this differently.
  'contact:create',
  'contact:update',
  'contact:delete',
  // `audit_log:read` was here until 0473: no part of Tvitvi asks for it, so the switch changed
  // nothing. Fixed at its default until a feature does. (`contact:export` and `audit_log:export`
  // were here too, and are gone since 249.10, 0992.)

  // Whether a volunteer coordinator may create work for others, or only carry out their own.
  'task:create',
  /* 85 (QA13): «Gi oppgaver til andre» er sin egen linje, ikke en setning i hjelpeteksten. */
  'task:assign',
  'task:delete',

  // Who may join the conversation on work they can see. Atle, 31 August, answering sak 56: «slik
  // hører hjemme i innstillinger». The default matches the old behaviour exactly — whoever may
  // change a task may comment on it — so this cell only ever widens, and only when asked to.
  'task:comment',

  // Whether limited users may start a thread or only reply in ones they were added to.
  'thread:create',

  // Atles spørsmål, ordrett: «Hvilke roller får automatisk tilgang til alle festivaler og venues
  // (alle årganger, også framtidige)?» Den ER en innstilling og ingenting annet — derfor står den
  // her. Uten denne raden ville Rettigheter vist bryteren grå, og basen ville nektet lagringen
  // (`adjustable` reiser med raden nettopp for at UI-et ikke kan tilby noe basen vil avvise).
  'festival:access_all',

  // Who may put a file on the shelf. A festival whose volunteers photograph every delivery note
  // answers this differently from one where the office files everything — and the default (`no`
  // for limited) is the narrow one, so this cell only ever widens.
  'document:create',
  // And who may take one off again. Separate from creating, because «I uploaded the wrong file»
  // and «this contract should not be here» are different sentences said by different people.
  'document:delete',

  // Who may see who changed what. Atle switched list/read ON for admin in decision 0003;
  // an organisation with a works council may want it narrower, another may want it wider.
  'audit_log:list',

  // Who may see the money. Atle, 30 August: "Ha dette som et option i innstillinger på rettigheter.
  // Slike ting skal helst ikke hardkodes." A festival whose production manager owns the budget
  // answers this differently from one where the office does — which is the test an entry has to
  // pass to belong on this list. → vedtak 0021.
  'economy:read',

  // Who may see what an artist costs, on the booking rather than in the year's totals. The same
  // question `economy:read` asks, asked one act at a time — and answered differently by a festival
  // whose production manager negotiates than by one where the office does.
  'contract:read',

  // Sak 196: «bare superadmin, men som celle i Rettigheter». Dekker deaktiver, gjenopprett og fjern.
  // Eskaleringsregelen i `rules.ts` står uansett: ingen fjerner noen på eller over egen rang.
  'membership:delete',

  // Sak 216.14 og 215.29: artisten i byråets stall. Atle ba om begge som innstillinger.
  'artist:invite',
  'artist:archive',
  // G2 (0923): Booking og Artister får egne linjer; «Avtaler og provisjon» blir justerbar (253.61).
  // Sak 237.2 «slette egne artister og skjule skannede» er `booking_artist:delete` (før `artist:delete`).
  'booking:list',
  'booking_artist:create',
  'booking_artist:update',
  'booking_draft:create',
  'booking_draft:update',
  'booking_enquiry:create',
  'booking_bid:create',
  'booking_bid:update',
  'booking_bid:respond',
  'booking_amount:read',
  'booking:delete',
  'booking_artist:delete',
  'artist:list',
  'artist:update',
  'agency_booking:create',
  'agency_booking:update',
  'agency_booking:respond',
  'artist_agreement:read',
  'artist_agreement:update',

  // «Overganger» på Booking: hvem ser byråbyttene. Samme standard som registeret (28. september).
  'agency_move:list',
  // F21: hvem som får varsel om bud og forespørsler uten ansvarlig agent.
  'booking_enquiry:notify',

  // Spillesteder og konserter (27. september): «Alle nye handlinger får justerbare celler i
  // Rettigheter med standardverdi: legge ut spillested, svare på datoforespørsel, se budsjett …»
  'venue:create',
  'venue:update',
  'venue_rate:read',
  // QA16 301.10: hvem som endrer leien og hvem som styrer synligheten, står i Rettigheter.
  'venue_rate:update',
  'venue:publish',
  'venue_enquiry:respond',
  'venue_registry:create',
  // One setting with create since 0473 («Føre steder utenfor Tvitvi»). `venue_registry:delete` stays
  // fixed: its default stops at admin, and merging it would move a new organisation's start value.
  'venue_registry:update',
  'date_request:create',
  'concert:create',
  'concert_budget:read',
  'concert_budget:update',
  'concert_template:create',
  // One setting with create since 0473 («Lage og endre konsertmaler»): same default per role.
  'concert_template:update',

  // F34 og F54 (runde 4. oktober, pakke 3): masseutsending, og hvem som ser ventende invitasjoner.
  'broadcast:create',
  'broadcast:list',
  'invitation:list',

  // Sak 232 (29. september): delingene. Atle godkjente cellene med standardverdiene — og «tilgang
  // hardkodes aldri», så hver av dem er en bryter i Rettigheter.
  'external:invite',
  'share_link:create',
  'resource:share',
  'shift:share',
  'department:share',
  // F14: kjøreplanen for én scene som lenke.
  'stage:share',
  'share:edit_level',
  // F9, F17 og F18 (runde 4. oktober): oversikten «Delinger», den delte advancen videre, og salen
  // og arrangementet fra spillestedet.
  'share:list',
  'shared_advance:share',
  'venue:share',
  'venue_night:share',
  // 239.37: delte advancer fra andre organisasjoner. 234.1: å slå sammen to kontakter.
  'shared_advance:read',
  'contact:merge',
  // F12 og F13 (runde 4. oktober, pakke 2): dele dokumenter, mapper og kartet.
  'document:share',
  'folder:share',
  'map:share',

  // Ti valg til (0475, Atle 30.09: «Ja, legg dem til»). Standarden er uendret; bare hvem som
  // bestemmer den flytter seg. `contact:invite` står med `invitation:*` fordi det er DEN ruta
  // POST /v1/invitations spør — uten den ville «Invitere nye medlemmer» vært en bryter som ikke
  // flyttet døra.
  'invitation:create',
  'invitation:delete',
  'contact:invite',
  'festival:manage_roles',
  'performance:create',
  'performance:update',
  'performance:delete',
  // F22 «Svare på motbud» er `booking_bid:respond` fra 0923; `performance:respond` brukes ikke lenger.
  'economy:create',
  'economy:update',
  'economy:delete',
  'contract:update',
  'contract:draft',
  'contract:register',
  'contract:void',
  'shift:create',
  'shift:update',
  'shift:delete',
  // QA16 297.4: påmelding og bytte av vakter, og hvem som godkjenner dem.
  'shift:request',
  'shift:assign',
  'accreditation:create',
  'accreditation:update',
  'venue_night:create',
  'venue_night:update',
  // Runde 4. oktober, pakke 3 (F49, F36): slette arrangementer, også en hel serie, og lage, gi nytt
  // navn til og slette ressurstyper. Standarden er den samme; bare hvem som bestemmer den flytter seg.
  'venue_night:delete',
  'resource_type:create',
  'resource_type:update',
  'resource_type:delete',
  'concert:update',

  // Runde 4. oktober (sak 239.21, 239.36, 239.38): hvem som ser og endrer Rettigheter, hvem som
  // endrer opplysninger om en superadmin, og hvem som legger til samarbeidspartnere.
  'organization_partner:create',
  'organization_partner:respond',
  'role_permission:list',
  'role_permission:read',
  'role_permission:update',
  'superadmin_profile:update',

  // Eksport (docs/EKSPORT.md §6): «Hvem som kan eksportere, er en celle i Rettigheter.»
  'export:create',
]);

export const isAdjustableAction = (action: Action): boolean => ADJUSTABLE_ACTIONS.has(action);

/**
 * Deliberately a local copy of `splitAction()` rather than an import from `./types.js`: that
 * module imports `LOCKED_ACTIONS` from here at runtime, and importing a *value* back would make
 * the cycle real — `buildDefaultRows()` runs at module load and would hit a temporal dead zone.
 * Types cross the boundary freely; values must not.
 */
function splitTemplateAction(action: Action): { resource: ResourceType; verb: Verb } {
  const index = action.indexOf(':');
  return {
    resource: action.slice(0, index) as ResourceType,
    verb: action.slice(index + 1) as Verb,
  };
}

/**
 * The template as `role_permissions` rows — what `app.create_organization()` inserts.
 *
 * Every non-locked cell produces a row, including the denied ones: a cell is switched off, never
 * removed (§6.6a has no DELETE path and no INSERT path after creation), so an organization that
 * wants to open `contact:delete` for `member` in Settings needs the row to already exist. Locked
 * actions produce no rows at all, for any role — the database rejects them, and they are decided
 * in code.
 */
function buildDefaultRows(): readonly PermissionRow[] {
  const rows: PermissionRow[] = [];
  for (const action of TEMPLATE_ACTIONS) {
    if (isLockedAction(action)) continue;
    const { resource, verb } = splitTemplateAction(action);
    const roleCells: RoleCells = DEFAULT_PERMISSION_MATRIX[action];
    for (const role of ORG_ROLES) {
      rows.push({ role, resource, action: verb, allowed: roleCells[role] !== 'no' });
    }
  }
  return Object.freeze(rows);
}

/** The template, all four roles, exactly as it is seeded. */
export const DEFAULT_ROLE_PERMISSIONS: readonly PermissionRow[] = buildDefaultRows();
